/**
 * chrome.tsx — the persistent PATIENT-FACING site chrome + the app shell split.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: UX Wave 5 (app-shell
 * redesign, 2026-07-21 — the staff surfaces left this chrome for the rail).
 *
 *   • EmergencyBar  — the top "Not for emergencies · 999/111" safety ribbon.
 *                     PATIENT-FACING SURFACES ONLY (Wave 5 §3): it renders on
 *                     the journey, /auth and the post-login patient pages, and
 *                     NEVER on /admin or /clinicians — staff are not the
 *                     audience for the safety signposting, and the CTO asked
 *                     repeatedly for it gone there. A regression test guards
 *                     both directions (chrome.spec.tsx).
 *   • SiteHeader    — the brand header, logo centred as a home link. Lives
 *                     here ONCE (not per-page) so every patient-facing surface
 *                     shows exactly one header. Side slots: signed OUT → the
 *                     "Speak to a clinician now" CTA (right); signed IN (any
 *                     role browsing a public page) → CTA left + profile menu
 *                     right. Staff routes DO NOT mount this header at all —
 *                     their logo lives on the white masthead block of the
 *                     staff top bar (UX Wave 7B — ui/StaffMasthead.tsx)
 *                     beside the account menu (ui/SideNav.tsx's layout).
 *   • SiteFooter    — the legal/trust footer (Terms · Privacy · … + trading line).
 *                     Patient-facing only — the staff console is a working
 *                     tool, not a public surface, and the legal footer adds noise.
 *   • StagingBadge  — the fixed "Staging Environment" ribbon, non-prod hosts only.
 *   • CoreHostBar   — Clinics-in-Core (Clinics ADR-1) ONLY: the slim "Back to
 *                     Consultations" strip above EVERY branch of the shell when
 *                     GPS Core serves this build under <core-host>/clinics/*.
 *                     A standalone build renders nothing here.
 *
 * AppShell wraps the whole app (see main.tsx) and SPLITS by route family:
 * a STAFF route (/admin, /clinicians) renders bare full-bleed content (the
 * page mounts its own rail shell via SideNavLayout) + the staging badge; every
 * other route gets the classic column — ribbon, header, content, footer. On the
 * Core-hosted image every branch is additionally topped by the CoreHostBar.
 *
 * STAFF-ROUTE DETECTION: a plain `pathname.startsWith('/admin' | '/clinicians')`
 * check via `useLocation` — AppShell always renders inside the Router (see
 * main.tsx / testing/render.tsx), so the hook is safe to call unconditionally
 * here. This is chrome-only (pathname-based) and independent of auth status.
 *
 * R28: category-level copy only — no POM name or price appears here.
 */
import { useEffect, useRef, useState, useSyncExternalStore, type CSSProperties, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getClinicianProfile, requestOwnPasswordReset } from '../api/identity';
import { DEFAULT_CLINIC_SLUG } from '../admin/clinics';
import { useAuth } from '../auth/AuthContext';
import {
  getProfilePhotoUrl,
  setProfilePhotoUrl,
  subscribeProfilePhoto,
} from '../clinicians/profilePhoto';
import { BrandLockup, Button, ButtonArrow, buttonClass, buttonStyle } from './kit';
import { ConfirmDialog, DialogShell } from './ConfirmDialog';
import { Menu, MenuItem, menuItemStyle } from './Menu';
import { isTrackedPath, loadGtm, shouldDropGtmDocument } from '../tracking/gtm';
import { ConsentBanner } from '../tracking/ConsentBanner';
import { ZoneExitLink } from '../tracking/ZoneExitLink';
import { CLINICAL_SAFETY_LEGACY_PATH, CLINICAL_SAFETY_PATH } from '../safety/route';
import { BOOKING_ORIGIN, PRIVACY_URL, TERMS_URL } from '../lib/bookingSite';
import { LAYER_STAGING_BADGE, LAYER_STICKY_TOP } from './layers';
import { ROUTED_CONTENT_ID, useResetViewOnRouteChange } from './viewReset';
import { hostReturnPath, isCoreHosted } from '../config';

/** True on /patients and its sub-paths — the signed-in patient portal, which
 * since 2026-07-25 wears the SAME full-width rail+masthead shell as the staff
 * consoles (it drops the centred SiteHeader + the legal footer), but KEEPS the
 * R31 emergency ribbon that every patient-facing surface must show. */
function useIsPatientPortalRoute(): boolean {
  const location = useLocation();
  return location.pathname.startsWith('/patients');
}

/**
 * True on /admin, /clinicians, /pharmacy, /auth-pharmacy and any of their
 * sub-paths. Shared by SiteHeader + AppShell.
 *
 * The pharmacy lane (2026-07-29) belongs here for the same reason the staff
 * consoles do, and for one of its own. Like them, /pharmacy mounts its own
 * SideNavLayout shell, so the centred SiteHeader and the legal footer would be
 * duplicate chrome. Unlike them, its audience is a third-party professional in a
 * dispensary: the patient-facing "Speak to a clinician now" callback CTA, the
 * R31 emergency ribbon and the cookie-consent banner are all addressed to a
 * patient and are simply wrong on a pharmacist's screen. /auth-pharmacy is
 * included even though it is a single form — it is the same audience, and it
 * paints its own centred brand lockup.
 */
function useIsStaffRoute(): boolean {
  const { pathname } = useLocation();
  return (
    pathname.startsWith('/admin') ||
    pathname.startsWith('/clinicians') ||
    pathname.startsWith('/pharmacy')
  );
}

/**
 * The pharmacy SIGN-IN page — chrome-wise its own case, and deliberately not the
 * bare page it started as.
 *
 * It was originally grouped with the staff consoles, which was half right and
 * produced something belonging to neither world. The consoles can drop this
 * chrome because they mount their own SideNavLayout + StaffMasthead, so they
 * still read as one product. This page mounts NO shell, so dropping it left a
 * form floating on a blank background that did not look like the platform at
 * all — and clinicians and admins sign in through /auth WITH the full chrome, so
 * the pharmacist was the only person meeting a different front door.
 *
 * So it now wears the same masthead and footer as /auth. What it does NOT get is
 * the two genuinely patient-addressed elements: the R31 emergency ribbon and the
 * "Speak to a clinician now" callback CTA. A pharmacist in a dispensary cannot act
 * on either.
 */
function useIsPharmacyAuthRoute(): boolean {
  return useLocation().pathname.startsWith('/auth-pharmacy');
}

/**
 * The PUBLIC clinical-safety page, and the CTO's original misspelling of its URL.
 *
 * WHY THE SHARED CHROME HAS TO KNOW ABOUT ONE CONTENT PAGE. That page
 * (safety/ClinicalSafetyPage.tsx) names prescription-only medicines in public,
 * which UK law permits for INFORMATION about a medicine and forbids for anything
 * that promotes its supply. The line is not drawn by tone; it is drawn by whether
 * the page carries an offer — and the page can only hold that line inside its own
 * `<main>`. It cannot hold it HERE.
 *
 * This header is where the estate's primary conversion control lives: "Speak to a
 * clinician now", a Link into the callback lane that opens the journey to
 * checkout, plus the sign-in pill beside it. Rendered above the fold on a page
 * listing named medicines, that is precisely the offer whose absence is the whole
 * basis for publishing the page at all — and it would have been the one thing on
 * it that no spec in `safety/` could see, because those specs scope to `<main>`
 * and this chrome is mounted outside it.
 *
 * So this route drops the two patient ACTIONS and keeps everything else: the
 * brand lockup (a reader must be able to identify who published a page naming
 * POMs — that is a transparency obligation), the R31 emergency ribbon (somebody
 * may be reading at 2am because something is happening to them), the profile menu
 * for a reader who is already signed in, and the legal footer. Same seam and the
 * same shape of reason as /auth-pharmacy above: there the visitor cannot act on
 * that CTA, here they must not be invited to.
 */
function useIsClinicalSafetyRoute(): boolean {
  const { pathname } = useLocation();
  return pathname === CLINICAL_SAFETY_PATH || pathname === CLINICAL_SAFETY_LEGACY_PATH;
}

/**
 * The top safety ribbon — persistent on every PATIENT-FACING screen (v3 ·
 * Patient Platform). NEVER on /admin or /clinicians (Wave 5 §3 — see
 * AppShell's staff branch; chrome.spec.tsx guards both directions).
 * Amber tint (#FBEAD0) with AA-contrast text (#8A5A00 ≈ 5:1 on the tint).
 *
 * While mounted it publishes its MEASURED height as `--gps-emergency-h` on the
 * root element, tracked live by ResizeObserver. The ribbon is pinned by
 * StickyTop at z-50, so every other sticky bar (the patient-shell masthead,
 * rail and mobile topbar) must stick at this offset to stack BELOW it rather
 * than hide behind it — and the height is NOT a constant: the text wraps at
 * narrow widths and shrinks at ≤560px, so consumers keep a 32px fallback (the
 * one-line desktop height) only for the pre-measure first paint.
 */
export function EmergencyBar() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const publish = () =>
      document.documentElement.style.setProperty('--gps-emergency-h', `${el.getBoundingClientRect().height}px`);
    publish();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(publish) : null;
    ro?.observe(el);
    return () => {
      ro?.disconnect();
      document.documentElement.style.removeProperty('--gps-emergency-h');
    };
  }, []);
  return (
    <div
      ref={ref}
      role="note"
      aria-label="Emergency information"
      className="gps-emergency-bar"
      style={{
        width: '100%',
        textAlign: 'center',
        padding: '7px 16px',
        background: '#FBEAD0',
        color: '#8A5A00',
        fontFamily: 'var(--gps-font-body)',
        fontSize: '0.8125rem',
        lineHeight: 1.4,
      }}
    >
      Not for emergencies. Call <strong>999</strong> for life-threatening situations ·{' '}
      Call <strong>111</strong> for urgent advice
    </div>
  );
}

/**
 * Where "Speak to a clinician now" actually goes.
 *
 * It used to point at `booking.thegpservice.co.uk/treatment/weight-loss`. That
 * host STRIPS the path (and the `#request-callback` anchor with it), so the
 * estate's primary conversion CTA landed every patient who pressed it on a
 * marketing home page with no callback form in sight — a dead link wearing the
 * clothes of the most important button on the site. `/:clinicSlug/callback`
 * (routes.tsx → journey/CallbackPage) is the same offer on a page we own and
 * can test.
 *
 * This chrome is NOT clinic-scoped — it renders above the router outlet on
 * /auth and /patients as well as the journey — so it resolves the slug the way
 * every other non-scoped surface here does, via the single-brand
 * `DEFAULT_CLINIC_SLUG`, rather than minting a second constant that could drift
 * away from it.
 */
const CALLBACK_PATH = `/${DEFAULT_CLINIC_SLUG}/callback`;

/**
 * The "Speak to a clinician now" CTA — an INTERNAL navigation to our own
 * callback lane. Deliberately not `target="_blank"`: it is an in-app form, and
 * spawning a tab for it strands the patient's journey in the one behind.
 *
 * `variant` decides its ≤560px fate (theme/tokens.css):
 *   - `'inline'` — the SIGNED-IN left-slot CTA, which collapses into the
 *     hamburger on mobile (the panel carries the same action).
 *   - `'standalone'` — the SIGNED-OUT right-slot CTA, the primary conversion
 *     action, which STAYS visible on mobile (just compacted) rather than
 *     hiding behind a hamburger.
 */
function BookingCta({ justify, variant }: { justify: 'start' | 'end'; variant: 'inline' | 'standalone' }) {
  return (
    <Link
      to={CALLBACK_PATH}
      // Class hooks for the mobile-only rules (theme/tokens.css @media ≤560px):
      // the desktop inline styles below are UNCHANGED — the mobile rules override
      // with !important (collapse the inline one, compact the standalone one).
      className={buttonClass('cta', `gps-booking-cta gps-booking-cta--${variant}`)}
      style={{
        ...buttonStyle('cta'),
        width: 'auto',
        minHeight: '44px',
        padding: '10px 16px',
        whiteSpace: 'nowrap',
        justifySelf: justify,
        flex: 'none',
      }}
    >
      Speak to a clinician now
      <ButtonArrow />
    </Link>
  );
}

/**
 * SiteHeader — the persistent PATIENT-FACING brand header (v3 parity): the GP
 * Service lockup pinned LEFT (a home link — clicking it, or the `/` route,
 * restarts the journey at /weightloss, see App.tsx). Lives ONCE in AppShell and
 * ONLY off the staff routes (Wave 5 §5 — the staff shell owns its own masthead).
 *
 * DESKTOP: logo left, an actions cluster RIGHT —
 *   - SIGNED IN (any role): the callback CTA + the profile menu.
 *   - Signed OUT: the callback CTA + a white "Log in" button (for returning
 *     patients — the journey's only sign-in entry).
 * MOBILE (≤560px, theme/tokens.css): the actions cluster collapses into the
 * MobileHeaderMenu hamburger (which carries the same actions for BOTH states).
 */
export function SiteHeader({ patientActions = true }: { patientActions?: boolean } = {}) {
  const { me } = useAuth();
  return (
    <header style={{ background: 'var(--gps-header-bg)' }}>
      <div
        className="gps-site-header"
        style={{
          maxWidth: '1100px',
          margin: '0 auto',
          padding: 'var(--gps-s-3) var(--gps-s-4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--gps-s-3)',
        }}
      >
        <Link
          to="/"
          className="gps-site-logo"
          aria-label="GP Service — go to start"
          style={{ display: 'inline-flex', textDecoration: 'none' }}
        >
          <BrandLockup />
        </Link>

        {/* DESKTOP actions (hidden ≤560px — the hamburger carries them there). */}
        <div className="gps-header-actions" style={{ display: 'flex', alignItems: 'center', gap: 'var(--gps-s-3)' }}>
          {/* The callback CTA and the sign-in pill are addressed to a PATIENT.
              They are suppressed on the pharmacy sign-in, where the visitor is a
              third-party professional in a dispensary: "Speak to a clinician now"
              is not an action they can take, and a patient log-in link would send
              them to the wrong front door. Everything else — the masthead, the
              brand lockup, the footer — stays, because that shared chrome is what
              makes this recognisably the same platform. */}
          {patientActions ? <BookingCta justify="end" variant="standalone" /> : null}
          {me ? (
            <ProfileMenu />
          ) : patientActions ? (
            // White "Log in" button (styling + hover in tokens.css .gps-header-login).
            <Link to="/auth?mode=login" className="gps-header-login">
              Log in
            </Link>
          ) : null}
        </div>

        {/* The hamburger carries the same patient actions at ≤560px, so it is
            suppressed for the same reason. Nothing inside it applies to a
            pharmacist: no patient profile, no booking, and the pharmacy session
            is not the `me` this menu reads. */}
        {patientActions ? <MobileHeaderMenu /> : null}
      </div>
    </header>
  );
}

/** Three-line hamburger glyph for the mobile header menu. Decorative — the button's aria-label carries the name. */
function HamburgerIcon() {
  return (
    <svg aria-hidden="true" role="img" viewBox="0 0 24 24" width={25} height={25} xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}>
      <path fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" d="M3.5 7h17M3.5 12h17M3.5 17h17" />
    </svg>
  );
}

const hamburgerTriggerStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '44px',
  height: '44px',
  padding: 0,
  border: 'none',
  borderRadius: 'var(--gps-r-md)',
  background: 'transparent',
  color: 'var(--gps-on-accent)',
  cursor: 'pointer',
};

/**
 * MobileHeaderMenu — the hamburger shown ONLY on ≤560px (CSS-toggled via
 * `.gps-header-hamburger`; hidden on desktop where the inline CTA + profile menu
 * take over). It reuses the shared accessible {@link Menu}; its panel (which
 * only renders when open) carries who's signed in, the callback CTA, the
 * role-aware profile links + Sign out.
 *
 * SIGNED-OUT it renders NOTHING: a logged-out visitor's only header action is
 * the callback CTA, which now stays visible on mobile (BookingCta `standalone`),
 * so a hamburger holding a single CTA would be redundant — and burying the
 * primary conversion action behind it is exactly what the review flagged.
 *
 * Because the panel is unmounted while closed AND the whole menu is absent when
 * signed out, it never duplicates the desktop CTA/profile in the DOM (the
 * branding tests assert exactly one of each).
 */
export function MobileHeaderMenu() {
  const { me, logout } = useAuth();
  return (
    <div className="gps-header-hamburger">
      <Menu
        menuLabel="Menu"
        triggerLabel="Menu"
        triggerStyle={hamburgerTriggerStyle}
        triggerContent={<HamburgerIcon />}
        menuStyle={{ minWidth: '13rem' }}
      >
        {({ close }) =>
          me ? (
          <>
            {/* The profile avatar is hidden on mobile — surface who's signed in
                here instead, at the top of the hamburger. */}
            <div style={{ padding: '4px 12px 10px' }}>
              <span style={{ display: 'block', fontSize: '11px', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--gps-muted)' }}>
                Signed in
              </span>
              <span style={{ display: 'block', fontSize: '14px', fontWeight: 700, color: 'var(--gps-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {me.fullName ?? me.email}
              </span>
            </div>
            <div role="separator" aria-orientation="horizontal" style={{ height: '1px', margin: '2px 4px 6px', background: 'var(--gps-border)' }} />
            <Link to={CALLBACK_PATH} role="menuitem" onClick={close} style={menuItemStyle}>
              Speak to a clinician now
            </Link>
            <div role="separator" aria-orientation="horizontal" style={{ height: '1px', margin: '6px 4px', background: 'var(--gps-border)' }} />
            {me.role === 'PATIENT' ? (
              <>
                <Link to="/patients/profile" role="menuitem" onClick={close} style={menuItemStyle}>
                  Your profile
                </Link>
                <ZoneExitLink to="/patients" role="menuitem" onClick={close} style={menuItemStyle}>
                  Dashboard
                </ZoneExitLink>
              </>
            ) : null}
            {me.role === 'CLINICIAN' ? (
              <Link to="/clinicians?tab=profile" role="menuitem" onClick={close} style={menuItemStyle}>
                Your profile
              </Link>
            ) : null}
            <MenuItem
              onSelect={() => {
                close();
                logout();
              }}
            >
              Sign out
            </MenuItem>
          </>
          ) : (
            <>
              {/* GUEST: the same two actions as the desktop cluster. */}
              <Link to={CALLBACK_PATH} role="menuitem" onClick={close} style={menuItemStyle}>
                Speak to a clinician now
              </Link>
              <div role="separator" aria-orientation="horizontal" style={{ height: '1px', margin: '6px 4px', background: 'var(--gps-border)' }} />
              <Link to="/auth?mode=login" role="menuitem" onClick={close} style={menuItemStyle}>
                Log in
              </Link>
            </>
          )
        }
      </Menu>
    </div>
  );
}

/** The profile pill trigger chrome (avatar + name) — reused as the Menu trigger
 * face. EXPORTED (2026-07-29) so the pharmacy console's account menu wears the
 * IDENTICAL pill rather than re-deriving one; the pharmacy session lives in its
 * own store, so it needs its own menu component but not its own look. */
export const profileTriggerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  minHeight: '44px',
  padding: '6px 12px 6px 6px',
  borderRadius: 'var(--gps-r-pill)',
  // Reads correctly on BOTH grounds (Wave 5): the dark patient header and the
  // light staff top bar — a token border on the white pill works for each.
  border: '1.5px solid var(--gps-border)',
  background: 'var(--gps-surface)',
  color: 'var(--gps-text)',
  font: 'inherit',
  fontSize: '13.5px',
  fontWeight: 600,
  cursor: 'pointer',
  maxWidth: '14rem',
};

/**
 * ProfileMenu — the header account control: a button showing the signed-in
 * user's name (`fullName` when identity provides it, the email otherwise)
 * beside an avatar circle carrying its initial (the initial alone survives on
 * a very narrow header via `.gps-profile-email`'s media-query hide — see
 * tokens.css), opening the shared accessible {@link Menu} (`aria-haspopup`,
 * `aria-expanded`, Escape or an outside click closes, focus returns to the
 * trigger). Items are role-aware: a PATIENT gets "Your profile" + "Dashboard"
 * links above the divider; a CLINICIAN gets "Your profile" (their
 * /clinicians?tab=profile section — UX Wave 8 §2: the avatar menu is now the
 * ONLY way in, the rail item is gone); everyone gets "Sign out" (the auth
 * context's `logout`). The dropdown chrome + a11y now live once in
 * ui/Menu.tsx — the row actions on the Users tab reuse the very same
 * primitive. Wave 5 mounts this SAME menu on the compact staff top bar
 * (ui/SideNav.tsx's SideNavLayout).
 *
 * UX Wave 8 §1: a CLINICIAN's avatar circle renders their PROFILE PHOTO
 * thumbnail when one is on file — via the tiny clinicians/profilePhoto store
 * (the profile panel publishes new photos there the moment they are saved;
 * the menu lazily fetches the profile ONCE when the store is still unknown).
 * The signed URL is short-lived: <img> onError falls back to the initial.
 *
 * Renders nothing pre-sign-in (`me` is null) — there is no account to show a
 * menu for yet (RequireRole redirects to /auth for that case).
 */
/**
 * ChangePasswordDialog — the signed-in STAFF "Change password" self-service
 * (2026-08-06). It does NOT take the old password: on confirm it asks identity
 * to email a single-use reset link to the caller's OWN mailbox
 * (`requestOwnPasswordReset` → `POST /v1/auth/me/reset-link`), and the user
 * sets the new password from that emailed link. This is the send-a-link half of
 * the same lane the guest reset and the admin "Send password reset link" use —
 * we never handle a password in the clear here.
 *
 * Two accessible dialog states: the confirm step (ConfirmDialog) and, once
 * sent, a DialogShell "check your email" panel. Failure keeps the confirm
 * dialog open with an aria-live error so the user can retry.
 */
function ChangePasswordDialog({ email, onClose }: { email?: string | null; onClose: () => void }) {
  const [phase, setPhase] = useState<'confirm' | 'sending' | 'sent' | 'error'>('confirm');

  async function send() {
    setPhase('sending');
    try {
      await requestOwnPasswordReset();
      setPhase('sent');
    } catch {
      // The link never came back — let them try again (no partial state to undo).
      setPhase('error');
    }
  }

  if (phase === 'sent') {
    return (
      <DialogShell title="Check your email" onCancel={onClose}>
        <p style={{ color: 'var(--gps-text)', fontSize: '14.5px', margin: '0 0 var(--gps-s-3)' }}>
          We've emailed a secure link{email ? <> to <strong>{email}</strong></> : ''}. Open it to set a
          new password — for your security it expires in 60 minutes.
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="cta" onClick={onClose} style={{ width: 'auto', minHeight: '44px', padding: '10px 18px', fontSize: '14.5px' }}>
            Done
          </Button>
        </div>
      </DialogShell>
    );
  }

  return (
    <ConfirmDialog
      open
      title="Change your password"
      confirmLabel={phase === 'sending' ? 'Sending…' : 'Email me a link'}
      busy={phase === 'sending'}
      body={
        <>
          We'll email a secure link{email ? <> to <strong>{email}</strong></> : ''} so you can set a new
          password — you won't need your current one.
          {phase === 'error' ? (
            <span role="alert" style={{ display: 'block', marginTop: 'var(--gps-s-2)', color: 'var(--gps-danger-text)' }}>
              Sorry — we couldn't send the link. Please try again.
            </span>
          ) : null}
        </>
      }
      onConfirm={() => void send()}
      onCancel={onClose}
    />
  );
}

export function ProfileMenu() {
  const { me, logout } = useAuth();
  const photoUrl = useSyncExternalStore(subscribeProfilePhoto, getProfilePhotoUrl, () => undefined);
  const isClinician = me?.role === 'CLINICIAN';
  // The self-service "Change password" dialog is a sibling of the Menu so it
  // survives the menu closing on select (same pattern as the Users-tab actions).
  const [changingPassword, setChangingPassword] = useState(false);

  // Lazily resolve the clinician's photo once per session (store 'undefined'
  // = unknown). A 404 (no record) or any failure resolves to null — the
  // initial-letter avatar, never an error the header could crash on.
  useEffect(() => {
    if (!isClinician || getProfilePhotoUrl() !== undefined) return;
    let cancelled = false;
    getClinicianProfile()
      .then((profile) => {
        if (!cancelled) setProfilePhotoUrl(profile.photoUrl ?? null);
      })
      .catch(() => {
        if (!cancelled) setProfilePhotoUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [isClinician]);

  if (!me) return null;

  // DEFENSIVE: `me` may arrive without a fullName (the deployed identity may
  // not return it yet) or even without an email (an /auth/me response from an
  // older identity revision, or any future principal shape drift) — the
  // header must NEVER crash the whole app over a missing display field. Show
  // the name when available, the email otherwise, and fall back to the role's
  // first letter for the avatar.
  const displayName = me.fullName ?? me.email ?? '';
  const initial = (displayName || me.role || '•').charAt(0).toUpperCase();
  const showPhoto = isClinician && typeof photoUrl === 'string' && photoUrl.length > 0;

  return (
    <>
    <Menu
      menuLabel="Account menu"
      wrapperStyle={{ justifySelf: 'end' }}
      wrapperClassName="gps-header-profile-inline"
      triggerStyle={profileTriggerStyle}
      menuStyle={{ minWidth: '10rem' }}
      triggerContent={
        <>
          <span
            aria-hidden="true"
            style={{
              width: '28px',
              height: '28px',
              flex: 'none',
              borderRadius: 'var(--gps-r-pill)',
              background: 'var(--gps-primary)',
              color: 'var(--gps-on-accent)',
              display: 'grid',
              placeItems: 'center',
              fontSize: '13px',
              fontWeight: 700,
              overflow: 'hidden',
            }}
          >
            {showPhoto ? (
              <img
                src={photoUrl}
                alt=""
                data-testid="profile-menu-photo"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                // The signed URL is short-lived — an expired image degrades to
                // the initial, never a broken-image glyph.
                onError={() => setProfilePhotoUrl(null)}
              />
            ) : (
              initial
            )}
          </span>
          <span className="gps-profile-email" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {displayName || me.role}
          </span>
        </>
      }
    >
      {({ close }) => (
        <>
          {me.role === 'PATIENT' ? (
            <>
              {/* NOTE: /patients/profile lands with the patient-profile slice —
                  the link is wired here so the menu is complete on day one. */}
              <Link to="/patients/profile" role="menuitem" onClick={close} style={menuItemStyle}>
                Your profile
              </Link>
              <ZoneExitLink to="/patients" role="menuitem" onClick={close} style={menuItemStyle}>
                Dashboard
              </ZoneExitLink>
              <div role="separator" aria-orientation="horizontal" style={{ height: '1px', margin: '6px 4px', background: 'var(--gps-border)' }} />
            </>
          ) : null}
          {me.role === 'CLINICIAN' ? (
            <>
              {/* Wave 8 §2 — the avatar menu is the ONLY entry to the
                  clinician's profile section (the rail item was removed). */}
              <Link to="/clinicians?tab=profile" role="menuitem" onClick={close} style={menuItemStyle}>
                Your profile
              </Link>
            </>
          ) : null}
          {me.role === 'CLINICIAN' || me.role === 'ADMIN' ? (
            <>
              {/* Self-service "Change password" (2026-08-06) — staff only. It
                  emails a reset link to the caller's OWN mailbox; the actual
                  change happens off the emailed link, never here. Patients use
                  the guest reset lane, so this row is not offered to them. */}
              <MenuItem
                onSelect={() => {
                  close();
                  setChangingPassword(true);
                }}
              >
                Change password
              </MenuItem>
              <div role="separator" aria-orientation="horizontal" style={{ height: '1px', margin: '6px 4px', background: 'var(--gps-border)' }} />
            </>
          ) : null}
          <MenuItem
            onSelect={() => {
              close();
              logout();
            }}
          >
            Sign out
          </MenuItem>
        </>
      )}
      </Menu>
      {/* Mounted only while open so each open starts from the confirm step. */}
      {changingPassword ? (
        <ChangePasswordDialog email={me.email ?? null} onClose={() => setChangingPassword(false)} />
      ) : null}
    </>
  );
}

const FOOTER_LINKS = [
  // The ONE terms address, shared with checkout's commitment gate — see
  // lib/bookingSite.ts for why it is a constant. Privacy joined it on
  // 2026-09-11 (the marketing-consent boxes link to it too). The other three
  // are built here because this footer is their only call site.
  { label: 'Terms', href: TERMS_URL },
  { label: 'Privacy', href: PRIVACY_URL },
  { label: 'Cookies', href: `${BOOKING_ORIGIN}/cookie-policy` },
  { label: 'Complaints', href: `${BOOKING_ORIGIN}/complaints-policy` },
  { label: 'FAQs', href: `${BOOKING_ORIGIN}/faq` },
];

/**
 * The legal/trust footer. Content is verbatim from the v3 footer; styled for the
 * portal's light theme (muted text, a hairline top border, links in the link
 * colour). The 999/111 wording lives in the top ribbon, so it is NOT repeated.
 */
export function SiteFooter() {
  return (
    <footer
      style={{
        borderTop: '1px solid var(--gps-border)',
        padding: 'var(--gps-s-5) var(--gps-s-4) var(--gps-s-6)',
        textAlign: 'center',
        fontFamily: 'var(--gps-font-body)',
        color: 'var(--gps-muted)',
      }}
    >
      <nav aria-label="Footer" style={{ marginBottom: 'var(--gps-s-2)', fontSize: '0.8125rem' }}>
        {FOOTER_LINKS.map((l, i) => (
          <span key={l.label}>
            <a
              href={l.href}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--gps-link)', textDecoration: 'none' }}
            >
              {l.label}
            </a>
            {i < FOOTER_LINKS.length - 1 ? <span aria-hidden="true"> · </span> : null}
          </span>
        ))}
      </nav>
      <p style={{ maxWidth: '40rem', margin: '0 auto', lineHeight: 1.5, fontSize: '0.6875rem' }}>
        The GP Service is a trading name of Novamedica Ltd · Clinicians are independently registered
        with the GMC or GPhC · Dispensing by Pharmacy Xpress (GPhC-registered).
      </p>
    </footer>
  );
}

/**
 * StagingBadge — a fixed amber ribbon on the right edge, shown ONLY on non-prod
 * hosts (decided client-side from the hostname; returns null on prod so it can
 * never render there). aria-hidden + pointer-events:none => decorative, never
 * blocks. useSyncExternalStore with a `false` server snapshot keeps it hidden on
 * first paint (hydration-safe).
 */
/**
 * The single source of truth for "is this a non-prod host?" — a `stage.` prefix,
 * bare `localhost`, or a `127.*` loopback. Prod hosts (e.g.
 * `app.clinics.thegpservice.co.uk`) never match. Exported so any surface that
 * must vary copy/behaviour by environment (e.g. the checkout payment-stub note)
 * decides from the SAME predicate the staging ribbon uses — never a second,
 * drifting hostname check. Takes the hostname as an arg (defaulting to the live
 * one) so it stays trivially unit-testable.
 */
const NON_PROD = /^stage\.|^localhost$|^127\./;
export function isNonProdHost(hostname: string = window.location.hostname): boolean {
  return NON_PROD.test(hostname);
}
const subscribe = () => () => {};
const getSnapshot = () => isNonProdHost();
const getServerSnapshot = () => false;

export function StagingBadge() {
  const show = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  if (!show) return null;
  return (
    <div
      aria-hidden="true"
      style={{
        position: 'fixed',
        right: 0,
        top: '50%',
        transform: 'translateY(-50%)',
        writingMode: 'vertical-rl',
        background: '#b45309',
        color: '#fff',
        padding: '14px 6px',
        font: '600 12px/1 system-ui, -apple-system, sans-serif',
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        borderRadius: '6px 0 0 6px',
        boxShadow: '-2px 0 10px rgba(0,0,0,0.18)',
        zIndex: LAYER_STAGING_BADGE,
        pointerEvents: 'none',
      }}
    >
      Staging Environment
    </div>
  );
}

/**
 * AppShell — the route-family split (Wave 5):
 *
 *   - STAFF routes (/admin, /clinicians): BARE full-bleed content + the
 *     staging badge. No emergency ribbon (§3 — patient-facing surfaces only,
 *     regression-tested), no centred-logo header (§5 — the logo lives on the
 *     rail, the account menu on the compact top bar, both mounted by the
 *     page's SideNavLayout), no legal footer. The page owns the whole
 *     viewport so the rail can run edge-to-edge and full-height (§1).
 *   - Everything else (journey, /auth, checkout, the post-login patient
 *     pages — §6, the clean top-nav variant of the shell family): the classic
 *     column — emergency ribbon pinned to the top, the brand header below it,
 *     the routed content in the middle (grows to fill), the footer at the
 *     bottom, and the staging badge floating on non-prod hosts.
 */
/**
 * A client-side hop cannot unload GTM. Once this document has injected the
 * container, leaving an acquisition path reloads the page, so the container
 * does not LIVE ON into the signed-in or auth page: that page's DOM, the
 * /auth password field (Part F) and every later route start in a document
 * with no tag in it.
 *
 * WHAT THIS DOES NOT DO — read before relying on it:
 *   • It runs in a passive effect, AFTER the router has already pushed the
 *     untracked URL into history. The GTM document still sees that one URL
 *     (a history-change listener or a GA4 page_view can fire on it) before the
 *     reload. Only a full-document `ZoneExitLink` avoids the push; this is the
 *     backstop for hops that are not one of those links (header "Log in" and
 *     "Your profile", sign-out, the 401 redirect, Back/Forward).
 *   • It does not change who can read the session bearer. The checkout mints
 *     it on the tracked journey itself, with GTM loaded, and localStorage is
 *     shared across the origin.
 *
 * `location.reload()`, NOT `location.replace(location.href)`: replacing with
 * the current URL is a same-document FRAGMENT navigation whenever the URL
 * carries a `#` (measured in Chromium), so the container would stay loaded
 * while this function reported a reload. `reload()` always starts a new
 * document and keeps the fragment and `history.state`.
 *
 * A direct visit to a staff or auth route never loaded GTM (the flag is set
 * only by `loadGtm`, and every new document starts without it), so it is not
 * reloaded and cannot loop. Exported so chrome.spec can pin the helper; the
 * AppShell-level cases there pin that `useGtmLoader` actually calls it.
 */
export function reloadOffAcquisition(pathname: string): boolean {
  if (typeof window === 'undefined') return false;
  const loaded = Boolean((window as { __gpsGtmLoaded?: boolean }).__gpsGtmLoaded);
  if (!shouldDropGtmDocument(pathname, loaded)) return false;
  window.location.reload();
  return true;
}

/** A1 GTM loader: on the acquisition journey ONLY, load the first-party GTM
 * container once (idempotent, fail-closed on an unset id). The signed-in app
 * (patient portal + staff consoles) is never tracked — `isTrackedPath` excludes
 * `/patients`, `/clinicians`, `/admin` and `/auth`, so those sessions never even
 * load the container. A later client-side hop off that journey reloads the page
 * (`reloadOffAcquisition`) because the container cannot be removed; the router
 * has already pushed that URL by then, so it bounds exposure after the hop
 * rather than preventing the hop being seen. */
function useGtmLoader(): void {
  const { pathname } = useLocation();
  useEffect(() => {
    if (isTrackedPath(pathname)) {
      loadGtm();
      return;
    }
    reloadOffAcquisition(pathname);
  }, [pathname]);
}

/**
 * Pins the top chrome (emergency ribbon + green header) to the top of the viewport
 * so it stays visible as the page scrolls — it previously sat in normal flow and
 * scrolled away, leaving a returning patient with no header, no safety ribbon and
 * no way back. `position: sticky` keeps it in the document flow (no content jump,
 * no layout shift) while holding the top edge. Applied to every branch that
 * renders a top bar. `top: 0` + the shell's own background stop content showing
 * through the gap on overscroll.
 *
 * Its z-index is `LAYER_STICKY_TOP` from ui/layers.ts, not a literal, because the
 * value is part of a SAFETY ordering: the consent modal's scrim is pinned strictly
 * below it so a person in crisis can still see and press through to the R31 999
 * ribbon this bar carries, whether or not they have answered the cookie prompt.
 * Read that file before changing the number.
 */
function StickyTop({ children }: { children: ReactNode }) {
  // `gps-sticky-top` is a class HOOK only: while a journey is mounted the pair
  // sits on a soft shadow instead of a hard edge (tokens.css, P7 —
  // `:root:has(.gps-journey-root)`). A box-shadow, never a filter: this bar is
  // not an ancestor of anything fixed, but the rule stays paint-only anyway.
  return (
    <div
      className="gps-sticky-top"
      style={{ position: 'sticky', top: 0, zIndex: LAYER_STICKY_TOP, background: 'var(--gps-bg)' }}
    >
      {children}
    </div>
  );
}

/**
 * The element that holds whatever the router matched — one component instead of
 * the same `<div>` copy-pasted into each of the shell's four route-family
 * branches.
 *
 * IT CARRIES THE SHARED ID + `tabindex="-1"` so that `ui/viewReset.ts` can hand
 * it focus when a screen is replaced. A client-side screen swap announces
 * nothing and leaves the keyboard on a control that no longer exists; focusing
 * the top of the new content puts assistive tech on the new `<h1>` (which sits
 * first inside here) and restarts the tab order at the content rather than at
 * the top of the whole document. `tabindex="-1"` keeps it programmatically
 * focusable WITHOUT adding a stop to the tab order.
 *
 * `outline: 'none'` because this container is never reached by tabbing — the
 * focus ring belongs on the controls a keyboard user actually moves between, and
 * a box drawn round the entire page on every navigation is noise, not a cue
 * (WCAG 2.4.7 is about interactive elements, which this is not).
 */
function RoutedContent({ children }: { children: ReactNode }) {
  return (
    <div
      id={ROUTED_CONTENT_ID}
      tabIndex={-1}
      style={{ flex: 1, display: 'flex', flexDirection: 'column', outline: 'none' }}
    >
      {children}
    </div>
  );
}

/**
 * CoreHostBar — rendered ONLY on the image GPS Core serves under
 * `<core-host>/clinics/*` (Clinics-in-Core, Clinics ADR-1, 2026-09-07): a slim strip at
 * the very top of EVERY branch of the shell, naming the workspace the person is
 * in and offering the way back to Core's own surface. It is what the Core
 * portal's "Consultations | Clinics" switch looks like from this side, because
 * under /clinics/* the Core shell is not on the page at all — this document is
 * entirely ours.
 *
 * A FULL-DOCUMENT `<a>`, never a `<Link>`: the Consultations workspace is a
 * different document on the same host, and `hostReturnPath()` is same-origin by
 * construction (config.ts refuses anything that is not a root-relative path).
 * Standalone builds render nothing — chrome.spec.tsx guards both states.
 */
export function CoreHostBar() {
  if (!isCoreHosted()) return null;
  return (
    <div
      role="navigation"
      aria-label="Workspace"
      className="gps-core-host-bar"
      style={{
        minHeight: '36px',
        padding: '6px 16px',
        background: 'var(--gps-primary)',
        color: 'var(--gps-on-accent)',
        fontFamily: 'var(--gps-font-body)',
        fontSize: '13px',
        lineHeight: 1.4,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexWrap: 'wrap',
        gap: '4px 14px',
      }}
    >
      <span>
        You&rsquo;re in the <strong>GPS Clinics</strong> workspace
      </span>
      <a href={hostReturnPath()} style={{ color: 'inherit', fontWeight: 600, textDecoration: 'underline' }}>
        Back to Consultations
      </a>
    </div>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  useGtmLoader();
  // Every route change lands at the top of the new page, with focus on it — the
  // browser does that for free on a real page load and a SPA has to do it by
  // hand. Skipped when the URL names a fragment, so an in-page anchor still wins
  // (see ui/viewReset.ts).
  useResetViewOnRouteChange();
  const isStaff = useIsStaffRoute();
  const isPatientPortal = useIsPatientPortalRoute();
  const isPharmacyAuth = useIsPharmacyAuthRoute();
  // NOT a styling switch: see useIsClinicalSafetyRoute for why a public page that
  // names prescription-only medicines may not wear a conversion CTA.
  const isClinicalSafety = useIsClinicalSafetyRoute();
  // Same shared chrome as /auth so the pharmacist meets the platform they are
  // dispensing for — minus the emergency ribbon and callback CTA, which are
  // addressed to a patient. See useIsPharmacyAuthRoute.
  if (isPharmacyAuth) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--gps-bg)' }}>
        <StickyTop>
          <CoreHostBar />
          <SiteHeader patientActions={false} />
        </StickyTop>
        <RoutedContent>{children}</RoutedContent>
        <SiteFooter />
        <StagingBadge />
      </div>
    );
  }
  if (isStaff) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--gps-bg)' }}>
        <CoreHostBar />
        <RoutedContent>{children}</RoutedContent>
        <StagingBadge />
      </div>
    );
  }
  // Patient portal: the staff-parity full-width shell (the page mounts the rail
  // + white masthead), but the R31 emergency ribbon stays on top — the ONE
  // patient-facing safety element the staff shell drops. No centred SiteHeader
  // (the masthead's account menu carries identity) and no legal footer.
  if (isPatientPortal) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--gps-bg)' }}>
        <StickyTop>
          <CoreHostBar />
          <EmergencyBar />
        </StickyTop>
        <RoutedContent>{children}</RoutedContent>
        <StagingBadge />
      </div>
    );
  }
  return (
    // `--gps-page-ground` is set ONLY while a journey surface is mounted
    // (`:root:has(.gps-journey-root)` in tokens.css) — the soft gradient page
    // ground. Every other page on this branch (/auth, /preferences, the
    // clinical-safety page…) keeps the flat `--gps-bg`.
    <div
      className="gps-app-shell"
      style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--gps-page-ground, var(--gps-bg))' }}
    >
      <StickyTop>
        <CoreHostBar />
        <EmergencyBar />
        <SiteHeader patientActions={!isClinicalSafety} />
      </StickyTop>
      {/* Flex column (matching the staff branch) so a full-height child — e.g. the
          patient portal shell — can `flex: 1` and fill down to the footer. Other
          clean pages have no flex child, so they size to content as before. */}
      <RoutedContent>{children}</RoutedContent>
      <SiteFooter />
      <ConsentBanner />
      <StagingBadge />
    </div>
  );
}
